import React, {JSX, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {checkUsername, deleteAccount, updateAccount} from "../../api/accountApi";
import QrCodeDialog from "../dialogs/QrCodeDialog";
import PasswordChangeDialog from "../dialogs/PasswordChangeDialog";
import ConfirmationDialog from "../dialogs/ConfirmationDialog";
import MessageBox, {MessageType} from "../components/MessageBox";
import {NAV} from "../../constants/navigation";
import {QUERY_PARAMS} from "../../constants/queryParams";
import Menu from "../menu/Menu";
import {useRequiredAccount} from "../../hooks/AccountContext";
import {useSaveHandler} from "../../hooks/SaveHandlerContext";

interface FormData {
    email: string,
    username: string,
    lastName: string,
    firstName: string
}

function AccountData(): JSX.Element {
    const {t} = useTranslation();

    const [formData, setFormData] = useState<FormData>({
        email: "",
        username: "",
        lastName: "",
        firstName: "",
    });

    const [message, setMessage] = useState<MessageType>(null);
    const [disableSaving, setDisableSaving] = useState<boolean>(true);
    const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
    const [showQr, setShowQr] = useState(false);
    const [showPwDialog, setShowPwDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    const {
        account,
        setAccount,
        logout
    } = useRequiredAccount();

    const { setHasUnsavedChanges } = useSaveHandler();

    // Daten beim Mount laden
    useEffect(() => {
        const fetchData = async () => {
            setFormData({
                email: account.email,
                username: account.username,
                lastName: account.lastName,
                firstName: account.firstName,
            });
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [account]);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    ) => {
        const {name, value} = e.target;

        setDisableSaving(false);
        setHasUnsavedChanges(true);
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setMessage(null);

        try {
            const dto = {
                email: formData.email,
                username: formData.username,
                lastName: formData.lastName,
                firstName: formData.firstName,
            };

            let updated = await updateAccount(account.id, dto);
            setAccount(updated);
            setMessage({type: "success", text: t("common.messages.success")});
            setDisableSaving(true);
            setHasUnsavedChanges(false);
        } catch {
            setMessage({type: "error", text: t("common.messages.error")});
        }
    };

    const handleUsernameChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setFormData({...formData, username: value});

        if (value !== account.username && value.length > 2) {
            try {
                const exists = await checkUsername(value);
                setUsernameAvailable(!exists);
            } catch {
                setUsernameAvailable(null);
            }
        } else {
            setUsernameAvailable(null);
        }
    };

    return (
        <div>

            <div>
                <h2>{t("menu.account")}</h2>
            </div>

            <div className="menu-row">
                <Menu
                    items={[
                        {label: t("common.button.showQr"), action: () => setShowQr(true)},
                        {label: t("common.button.changePassword"), action: () => setShowPwDialog(true)},
                        {label: t("common.button.deleteAccount"), action: () => setShowDeleteDialog(true)}
                    ]}
                />
            </div>

            <form onSubmit={handleSubmit} className="form-container">
                <MessageBox message={message}/>

                <div className="form-group">
                    <label>{t("auth.fields.email")}</label>
                    <input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="form-input"
                        required
                    />
                </div>

                <div className="form-group">
                    <label>{t("auth.fields.username")}</label>
                    <div className="input-with-icon">
                        <input
                            name="username"
                            type="text"
                            value={formData.username}
                            onChange={handleUsernameChange}
                            autoComplete="off"
                            className="form-input"
                            required
                        />
                        {usernameAvailable === true && <span className="icon icon-green">✔</span>}
                        {usernameAvailable === false && <span className="icon icon-red">✖</span>}
                    </div>
                </div>

                <div className="form-group">
                    <label>{t("contact.fields.firstName")}</label>
                    <input
                        name="firstName"
                        type="text"
                        value={formData.firstName}
                        onChange={handleChange}
                        className="form-input"
                        required
                    />
                </div>

                <div className="form-group">
                    <label>{t("contact.fields.lastName")}</label>
                    <input
                        name="lastName"
                        type="text"
                        value={formData.lastName}
                        onChange={handleChange}
                        className="form-input"
                        required
                    />
                </div>

                {/* Submit */}
                <button
                    type="submit"
                    disabled={disableSaving}
                    className="form-button">
                    {t("common.button.save")}
                </button>
            </form>

            <QrCodeDialog
                open={showQr}
                code={account.accountCode}
                codeType={QUERY_PARAMS.ACCOUNT}
                qrDestinationPage={NAV.SHOW_PERSON}
                onClose={() => setShowQr(false)}
            />

            <PasswordChangeDialog
                open={showPwDialog}
                onClose={() => setShowPwDialog(false)}
            />

            {/*TODO: Übersetzungen*/}
            <ConfirmationDialog
                open={showDeleteDialog}
                title="Account löschen"
                message="Möchtest du deinen Account wirklich löschen?"
                onConfirm={async () => {
                    await deleteAccount(account.id);
                    logout();
                    // Weiterleitung oder Logout
                }}
                onClose={() => setShowDeleteDialog(false)}
            />
        </div>
    );
}

export default AccountData;